import { useState } from 'react';
import { useStore } from '../store/useStore';
import { MIN_NEW_PASSWORD, type EmailAuthMode } from '../sync/emailAuth';

/**
 * Settings → Cloud Sync: email + password (13 Oct 2026). Shown on EVERY
 * platform — it is the one sign-in that needs no Google account, no Apple
 * device and no browser hop. Collapsed by default so Google/Apple stay the
 * one-tap paths. Results and errors (syncNotice / syncError) are shown
 * INSIDE this form, directly under the button that caused them (CLAUDE.md
 * §2). 13 Oct 2026, first live test: a 6-character password was refused by
 * the 8-character rule, but the card was in its error state, which renders
 * syncError at the TOP of the card — off-screen — so "Create account"
 * looked like it did nothing.
 *
 * Inputs use 16px text below 640px: iOS zooms the page on focus for anything
 * smaller (same fix as the New-tag input, tagInputMobile.test.ts).
 */
export function EmailPasswordSignIn({ startOpen = false }: { startOpen?: boolean }) {
  const [open, setOpen] = useState(startOpen);
  const [mode, setMode] = useState<EmailAuthMode>('sign-in');
  // A remembered email+password account pre-fills its address (13 Oct 2026).
  const [email, setEmail] = useState(() => {
    const last = useStore.getState().lastAccount;
    return last?.provider === 'password' ? last.email : '';
  });
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  /** Set once the user has pressed a button here, so this form shows the outcome. */
  const [attempted, setAttempted] = useState(false);
  const syncError = useStore((st) => st.syncError);
  const syncNotice = useStore((st) => st.syncNotice);
  const shortBy = mode === 'create' && password.length > 0 ? MIN_NEW_PASSWORD - password.length : 0;

  const submit = async () => {
    if (busy) return;
    setAttempted(true);
    setBusy(true);
    try {
      const m = await import('../sync/firebaseSync');
      const result = await m.signInWithEmailPassword(email, password, mode);
      if (result === 'ok') setPassword('');
    } finally {
      setBusy(false);
    }
  };

  const forgot = async () => {
    if (busy) return;
    setAttempted(true);
    setBusy(true);
    try {
      const m = await import('../sync/firebaseSync');
      await m.sendPasswordReset(email);
    } finally {
      setBusy(false);
    }
  };

  const inputClass =
    'w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-base sm:text-xs text-slate-900 dark:text-white placeholder-slate-400 focus:border-brand-500 focus:outline-none';

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="text-[11px] font-bold text-brand-600 dark:text-brand-400 underline-offset-2 hover:underline"
      >
        Use email and password instead
      </button>
    );
  }

  return (
    <form
      className="space-y-2"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <div className="flex gap-1 rounded-lg bg-slate-200/60 dark:bg-slate-800/60 p-0.5" role="tablist">
        {(['sign-in', 'create'] as const).map((m) => (
          <button
            key={m}
            type="button"
            role="tab"
            aria-selected={mode === m}
            onClick={() => setMode(m)}
            className={`flex-1 rounded-md px-2 py-1.5 text-[11px] font-bold transition-colors ${
              mode === m
                ? 'bg-white text-slate-900 shadow-sm dark:bg-slate-900 dark:text-white'
                : 'text-slate-500 dark:text-slate-400'
            }`}
          >
            {m === 'sign-in' ? 'Sign in' : 'Create account'}
          </button>
        ))}
      </div>
      <input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="you@anywhere.com"
        autoComplete="email"
        autoCapitalize="none"
        spellCheck={false}
        aria-label="Email address"
        className={inputClass}
      />
      <div className="flex gap-2">
        <input
          type={showPassword ? 'text' : 'password'}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder={mode === 'create' ? 'New password (8+ characters)' : 'Password'}
          autoComplete={mode === 'create' ? 'new-password' : 'current-password'}
          aria-label="Password"
          className={inputClass}
        />
        <button
          type="button"
          onClick={() => setShowPassword((v) => !v)}
          className="whitespace-nowrap rounded-lg px-2 text-[11px] font-semibold text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
        >
          {showPassword ? 'Hide' : 'Show'}
        </button>
      </div>
      {shortBy > 0 && (
        <p className="text-[11px] font-semibold text-amber-700 dark:text-amber-300" role="status">
          {password.length} of {MIN_NEW_PASSWORD} characters — {shortBy} more needed.
        </p>
      )}
      {mode === 'create' && (
        <p className="text-[11px] leading-relaxed text-slate-500 dark:text-slate-400">
          Creates a NEW, empty ZoneIn account for this email. Already signed in
          somewhere with Google or Apple? Use that button instead, so your tasks
          and any lifetime access come with you.
        </p>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="submit"
          disabled={busy}
          className="rounded-lg bg-brand-600 px-4 py-2 text-xs font-bold text-white transition-colors hover:bg-brand-500 disabled:opacity-60"
        >
          {busy ? 'Please wait…' : mode === 'create' ? 'Create account' : 'Sign in'}
        </button>
        {mode === 'sign-in' && (
          <button
            type="button"
            onClick={() => void forgot()}
            disabled={busy}
            className="rounded-lg px-2 py-2 text-[11px] font-semibold text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 disabled:opacity-60"
          >
            Forgot password?
          </button>
        )}
      </div>
      {/* The outcome, right where the user is looking (see header note). */}
      {attempted && syncError && (
        <p
          role="alert"
          className="break-words rounded-lg bg-red-50 dark:bg-red-950/20 p-2 text-[11px] leading-relaxed text-red-700 dark:text-red-300"
        >
          {syncError}
        </p>
      )}
      {attempted && !syncError && syncNotice && (
        <p
          role="status"
          className="break-words rounded-lg border border-brand-100 dark:border-brand-500/20 bg-brand-50 dark:bg-brand-900/20 p-2 text-[11px] leading-relaxed text-brand-700 dark:text-brand-100"
        >
          {syncNotice}
        </p>
      )}
    </form>
  );
}
